import { useEffect } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { create } from 'zustand';
import { cn } from '@/lib/cn';

type ToastTone = 'success' | 'info' | 'warning';

interface ToastMessage {
  action?: { label: string; onClick: () => void };
  description?: string;
  duration?: number;
  id: string;
  title: string;
  tone: ToastTone;
}

interface ToastState {
  messages: ToastMessage[];
  dismiss: (id: string) => void;
  push: (message: Omit<ToastMessage, 'id'>) => string;
}

export const useToastStore = create<ToastState>((set) => ({
  messages: [],
  dismiss: (id) =>
    set((state) => ({ messages: state.messages.filter((message) => message.id !== id) })),
  push: (message) => {
    const id = crypto.randomUUID();
    set((state) => ({ messages: [...state.messages.slice(-3), { ...message, id }] }));
    return id;
  }
}));

export function toast(
  title: string,
  options: Partial<Pick<ToastMessage, 'action' | 'description' | 'duration' | 'tone'>> = {}
): string {
  return useToastStore.getState().push({
    title,
    tone: options.tone ?? 'success',
    duration: options.duration ?? 4500,
    description: options.description,
    action: options.action
  });
}

function ToastItem({ message }: { message: ToastMessage }) {
  const dismiss = useToastStore((state) => state.dismiss);
  const icons = { success: CheckCircle2, info: Info, warning: TriangleAlert };
  const Icon = icons[message.tone];
  const iconTone = { success: 'text-success', info: 'text-accent', warning: 'text-warning' };

  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(message.id), message.duration ?? 4500);
    return () => window.clearTimeout(timer);
  }, [dismiss, message.duration, message.id]);

  return (
    <ToastPrimitive.Root
      className="grid grid-cols-[auto_1fr_auto] items-start gap-3 rounded-xl border border-line bg-surface p-4 shadow-lift data-[state=closed]:opacity-0"
      duration={message.duration}
      onOpenChange={(open) => {
        if (!open) dismiss(message.id);
      }}
      open
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 size-4', iconTone[message.tone])} />
      <div className="min-w-0">
        <ToastPrimitive.Title className="text-sm font-semibold text-ink">
          {message.title}
        </ToastPrimitive.Title>
        {message.description ? (
          <ToastPrimitive.Description className="mt-0.5 text-xs leading-relaxed text-muted">
            {message.description}
          </ToastPrimitive.Description>
        ) : null}
        {message.action ? (
          <ToastPrimitive.Action
            altText={message.action.label}
            className="mt-2 text-xs font-bold text-accent hover:underline"
            onClick={message.action.onClick}
          >
            {message.action.label}
          </ToastPrimitive.Action>
        ) : null}
      </div>
      <ToastPrimitive.Close
        aria-label="Hinweis schließen"
        className="rounded-md p-1 text-muted hover:bg-elevated hover:text-ink"
      >
        <X aria-hidden="true" className="size-4" />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

export function ToastViewport() {
  const messages = useToastStore((state) => state.messages);
  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {messages.map((message) => (
        <ToastItem key={message.id} message={message} />
      ))}
      <ToastPrimitive.Viewport className="safe-bottom fixed inset-x-3 bottom-[5.75rem] z-[80] flex max-h-dvh flex-col gap-2 sm:inset-x-auto sm:bottom-5 sm:right-5 sm:w-96" />
    </ToastPrimitive.Provider>
  );
}
